import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";
import { Badge, Button, Card, Screen, Text } from "~/components/ui";
import { parseRecipeSteps, useRecipe } from "~/hooks/useRecipes";
import { radius, spacing, type ThemeColors } from "~/styles/tokens";
import { useTheme, useThemedStyles } from "~/styles/theme";

function difficultyVariant(
  value: string,
): "success" | "warning" | "error" | "default" {
  if (value === "easy") return "success";
  if (value === "medium") return "warning";
  if (value === "hard") return "error";
  return "default";
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
}

export default function RecipeDetailScreen() {
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { data: recipe, loading, error } = useRecipe(id);

  if (loading) {
    return (
      <Screen>
        <View style={styles.center}>
          <Text variant="caption">Loading recipe…</Text>
        </View>
      </Screen>
    );
  }

  if (error || !recipe) {
    return (
      <Screen>
        <View style={styles.center}>
          <Text style={styles.centerEmoji}>😕</Text>
          <Text variant="title" align="center">
            Recipe not found
          </Text>
          <Text variant="caption" align="center">
            {error}
          </Text>
        </View>
      </Screen>
    );
  }

  const ingredients = recipe.ingredients
    ? recipe.ingredients.split("\n").filter(Boolean)
    : [];
  const steps = parseRecipeSteps(recipe);
  const hasTimedSteps = steps.some((step) => step.waitMinutes > 0);

  return (
    <Screen scroll padded={false}>
      <View style={styles.hero}>
        <View style={styles.heroThumb}>
          <Text style={styles.heroEmoji}>{recipe.emoji || "🍞"}</Text>
        </View>
        <Text variant="heading" align="center">
          {recipe.title}
        </Text>
        <Text
          variant="body"
          align="center"
          color={colors.heading}
          style={styles.heroDesc}
        >
          {recipe.description}
        </Text>
        <View style={styles.badges}>
          <Badge
            label={
              recipe.difficulty.charAt(0).toUpperCase() +
              recipe.difficulty.slice(1)
            }
            variant={difficultyVariant(recipe.difficulty)}
          />
          <Badge label={`${recipe.prepTimeMinutes}m prep`} />
          <Badge label={`${recipe.fermentTimeHours}h ferment`} />
          <Badge label={`Serves ${recipe.servings}`} />
        </View>
      </View>

      <View style={styles.body}>
        <Button
          label="Start cooking"
          onPress={() =>
            router.push({
              pathname: "/recipe/[id]/cook",
              params: { id: recipe.id },
            })
          }
          variant="primary"
          fullWidth
        />
        {hasTimedSteps ? (
          <Text variant="caption" align="center" style={styles.cookHint}>
            🔔 Timed steps can ping you when each wait is done
          </Text>
        ) : null}

        <Text variant="label" style={styles.sectionLabel}>
          Ingredients
        </Text>
        <Card elevation="flat" padding="md">
          {ingredients.map((ingredient, index) => (
            <View
              key={ingredient}
              style={[
                styles.listRow,
                index < ingredients.length - 1 && styles.listRowBorder,
              ]}
            >
              <View style={styles.bullet} />
              <Text variant="body" style={styles.listText}>
                {ingredient}
              </Text>
            </View>
          ))}
          {ingredients.length === 0 ? (
            <Text variant="caption">No ingredients listed</Text>
          ) : null}
        </Card>

        <Text variant="label" style={styles.sectionLabel}>
          Steps
        </Text>
        <Card elevation="raised" padding="md">
          {steps.map((step, index) => (
            <View
              key={step.id}
              style={[
                styles.step,
                index < steps.length - 1 && styles.listRowBorder,
              ]}
            >
              <View style={styles.stepNumber}>
                <Text style={styles.stepNumberText}>{index + 1}</Text>
              </View>
              <View style={styles.stepBody}>
                <Text variant="body" style={styles.stepTitle}>
                  {step.title}
                </Text>
                {step.waitMinutes > 0 ? (
                  <Text variant="caption" color={colors.muted}>
                    ⏱ {formatDuration(step.waitMinutes)}
                  </Text>
                ) : null}
              </View>
            </View>
          ))}
          {steps.length === 0 ? (
            <Text variant="caption">No steps listed</Text>
          ) : null}
        </Card>
      </View>

      <View style={{ height: spacing.xxl }} />
    </Screen>
  );
}

const useStyles = () => useThemedStyles(makeStyles);

const makeStyles = (c: ThemeColors) =>
  StyleSheet.create({
    center: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.xl,
      gap: spacing.sm,
    },
    centerEmoji: {
      fontSize: 44,
      lineHeight: 52,
    },
    hero: {
      alignItems: "center",
      paddingTop: spacing.xl,
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.lg,
      backgroundColor: c.background,
      gap: spacing.xs,
    },
    heroThumb: {
      width: 76,
      height: 76,
      borderRadius: radius.xl,
      backgroundColor: c.surface,
      borderWidth: 1,
      borderColor: c.border,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: spacing.sm,
    },
    heroEmoji: {
      fontSize: 40,
    },
    heroDesc: {
      marginTop: spacing.xs,
    },
    badges: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.xs,
      marginTop: spacing.md,
      justifyContent: "center",
    },
    body: {
      paddingHorizontal: spacing.md,
      paddingTop: spacing.lg,
    },
    cookHint: {
      marginTop: spacing.sm,
    },
    sectionLabel: {
      marginTop: spacing.xl,
      marginBottom: spacing.sm,
      marginLeft: spacing.xs,
    },
    listRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.sm,
      paddingVertical: spacing.sm,
    },
    listRowBorder: {
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    listText: {
      flex: 1,
    },
    bullet: {
      width: 6,
      height: 6,
      borderRadius: 3,
      backgroundColor: c.accent,
    },
    step: {
      flexDirection: "row",
      gap: spacing.sm,
      alignItems: "flex-start",
      paddingVertical: spacing.sm + 2,
    },
    stepNumber: {
      width: 26,
      height: 26,
      borderRadius: radius.full,
      backgroundColor: c.primary,
      alignItems: "center",
      justifyContent: "center",
      marginTop: 1,
    },
    stepNumberText: {
      color: c.primaryForeground,
      fontSize: 12,
      fontWeight: "700",
    },
    stepBody: {
      flex: 1,
      gap: 2,
    },
    stepTitle: {
      flex: 1,
    },
  });
